import type { EvidenceSourceType, InsightEvidenceGroup, InsightEvidenceItem } from "~vue/api/generated/intelligence-evidence";
import { COLLABORATION_PROFILE_BOOTSTRAP_ICON_CLASS } from "~vue/components/navigation/menuPanes";

export type { EvidenceSourceType, InsightEvidenceGroup, InsightEvidenceItem };

export type InsightEvidenceLocation = "mywork" | "mywork_homepage_carousel" | "new_chat_prompt" | "onboarding";

export type InsightEvidenceAnalyticsContext = {
    insightId: string;
    experimentSlug: string;
    location: InsightEvidenceLocation;
    coachingSessionUuid?: string | null;
};

const EVIDENCE_SOURCE_WEIGHTS: Record<EvidenceSourceType, number> = {
    collaboration_profile: 1,
    past_chat: 2,
    connection_note: 3,
    lab_insight: 4,
    perspective: 5,
    calendar_event: 6,
    granola_meeting: 7,
    workiq_outlook: 7,
    workiq_teams: 7,
    workiq_calendar: 7,
    workiq_documents: 7,
    workiq_microsoft_365: 7,
    other: 7,
    performance_review_conversation: 8,
    performance_review_upload: 8,
};

export type EvidenceSourceCounts = Partial<Record<EvidenceSourceType, number>>;

export function sortEvidenceGroups(groups: InsightEvidenceGroup[]): InsightEvidenceGroup[] {
    return [...groups].sort((left, right) => EVIDENCE_SOURCE_WEIGHTS[left.kind] - EVIDENCE_SOURCE_WEIGHTS[right.kind]);
}

export function evidenceSourceCounts(groups: InsightEvidenceGroup[]): EvidenceSourceCounts {
    return groups.reduce<EvidenceSourceCounts>((counts, group) => {
        counts[group.kind] = (counts[group.kind] ?? 0) + group.count;
        return counts;
    }, {});
}

/** Perspective is excluded because EvidenceSourceIcon renders it with the PerspectiveCircles mark. */
export const EVIDENCE_ICON_CLASSES: Record<Exclude<EvidenceSourceType, "perspective">, string> = {
    calendar_event: "bi-calendar3",
    granola_meeting: "bi-journal-text",
    workiq_outlook: "bi-envelope",
    workiq_teams: "bi-people-fill",
    workiq_calendar: "bi-calendar-event",
    workiq_documents: "bi-file-earmark-text",
    workiq_microsoft_365: "bi-microsoft",
    past_chat: "bi-chat-left-text",
    connection_note: "bi-person-lines-fill",
    collaboration_profile: COLLABORATION_PROFILE_BOOTSTRAP_ICON_CLASS,
    lab_insight: "bi-lightbulb",
    performance_review_conversation: "bi-clipboard-data",
    performance_review_upload: "bi-clipboard-data",
    other: "bi-link-45deg",
};

export function evidenceChipLabel(group: InsightEvidenceGroup): string {
    return group.chip_label ?? group.label;
}

export function calculateVisibleEvidenceGroupCount(containerWidth: number, labelWidth: number, groupWidths: number[], overflowWidths: number[], gap = 8): number {
    const labelGapCount = labelWidth > 0 ? 1 : 0;
    const allGroupsWidth = labelWidth + groupWidths.reduce((total, width) => total + width, 0) + gap * Math.max(groupWidths.length + labelGapCount - 1, 0);
    if (allGroupsWidth <= containerWidth) {
        return groupWidths.length;
    }

    let visibleWidth = labelWidth;
    let visibleGroups = 0;
    for (let visibleCount = 0; visibleCount < groupWidths.length; visibleCount += 1) {
        const hiddenCount = groupWidths.length - visibleCount;
        const overflowWidth = overflowWidths[hiddenCount - 1] ?? 0;
        const requiredWidth = visibleWidth + overflowWidth + gap * (visibleCount + labelGapCount);
        if (requiredWidth > containerWidth) {
            break;
        }
        visibleGroups = visibleCount;
        visibleWidth += groupWidths[visibleCount] ?? 0;
    }

    return visibleGroups;
}
